/**
 * The look of a step's own footer action (BLU-1376, handover 4.1).
 *
 * A pane has ONE visually primary button. While a step is open, that is the
 * step's own action (Install, Save, Go to <page>). Once the step is complete
 * and there is a next step, the footer also offers "Next: <label>" and Next
 * becomes the primary; the step's own action stays, demoted to secondary.
 *
 * `StepFooter` publishes the tone through this context around the step's
 * action; `PaneActionButton` and `SavePrimaryButton` read it. Outside a footer
 * the tone is `primary`, so a button rendered anywhere else keeps today's look.
 */
import { createContext, useContext } from 'react';

export type PaneActionTone = 'primary' | 'secondary';

/** The footer primary: deep purple, white text. */
export const PANE_PRIMARY =
  'h-8 gap-1.5 border-transparent bg-primary-strong px-3 text-xs text-white hover:bg-primary-strong/90 hover:text-white';

/** A demoted step action beside "Next": outlined, same size, so the row stays even. */
export const PANE_SECONDARY =
  'h-8 gap-1.5 border border-line-strong bg-card px-3 text-xs text-foreground hover:bg-muted hover:text-foreground';

export const PaneActionToneContext = createContext<PaneActionTone>('primary');

export function usePaneActionTone(): PaneActionTone {
  return useContext(PaneActionToneContext);
}

export function paneActionClass(tone: PaneActionTone): string {
  return tone === 'secondary' ? PANE_SECONDARY : PANE_PRIMARY;
}
